import {useCallback, useState, useEffect} from "react";
import {OpenMatchRequests, OpenMatchStatusEnum, OpenMatchModelResponse} from "@/app/api/open_match";
import {UserRequests} from "@/app/api/user";
import {useNavigate} from "react-router-dom";
import {handleMatchClick} from "@/app/utils/custom_function";

interface ScreenState {
  hasError?: string;
  places: [];
  archivedMatches: OpenMatchModelResponse[];
  isLoading: boolean;
}

const usePreviousPlaces = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<ScreenState>({
    hasError: undefined,
    places: [],
    archivedMatches: [],
    isLoading: false,
  });

  const updateState = useCallback((updates: Partial<ScreenState>) => {
    setState((prev) => ({...prev, ...updates}));
  }, []);

  const fetchArchivedMatches = useCallback(async () => {
    updateState({isLoading: true, hasError: undefined});

    try {
      // Fetch all archived matches
      const matches = await OpenMatchRequests.getMatches({ status: OpenMatchStatusEnum.ARCHIVED });

      // Get current user
      const currentUser = await UserRequests.getCurrentUser();

      // Filter matches where current user is a participant (either as owner or in users array)
      const userMatches = matches.filter(match => {
        // Check if user is the owner
        const isOwner = match.owner?.id === currentUser.user.id;

        // Check if user is in the participants
        const isParticipant = match.users?.some(user => user.id === currentUser.user.id);

        return isOwner || isParticipant;
      });

      if (userMatches.length === 0) {
        updateState({ archivedMatches: [], hasError: 'No matches yet' });
      } else {
        updateState({archivedMatches: userMatches});
      }
    } catch (error: any) {
      updateState({hasError: error.message || "No matches yet"});
    } finally {
      updateState({isLoading: false});
    }
  }, [updateState]);

  const onMatchClick = useCallback((match: OpenMatchModelResponse) => {
    handleMatchClick(match, navigate);
  }, [navigate]);

  const init = useCallback(() => {
    updateState({places: []});
    fetchArchivedMatches();
  }, [updateState, fetchArchivedMatches]);

  useEffect(() => {
    init();
  }, [init]);

  return {
    state,
    init,
    onMatchClick
  };
};

export default usePreviousPlaces;